import React, { useState, useEffect, useContext } from "react";

import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";

import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import {
  StyledTableCell,
  StyledTableRow,
} from "components/atoms/table/StyledTable";
import Paper from "@mui/material/Paper";
import Pagination from "components/atoms/table/Pagination";

import { SnackContext } from "context/SnackProvider";
import instance, { GetUserRole, getErrorMessage } from "api/definitions";
import MessageDialog, {
  MessageDialogVariants,
} from "components/atoms/info/MessageDialog";

const sortListByName = (currentList) => {
  const newList = [...currentList].sort((a, b) => {
    return a.nome.localeCompare(b.nome);
  });
  return newList;
};

const DeleteLocationResource = (props) => {
  const [isLoaded, setIsLoaded] = useState(false);

  const snackContext = useContext(SnackContext);

  useEffect(() => {
    setIsLoaded(false);
    if (props.loadDelete) {
      const fullURLDelete = `local/${props.idLocation}/recurso/${props.idLocationResource}`;
      instance
        .delete(fullURLDelete)
        .then(function (response) {
          setIsLoaded(true);
          const result = response.data;
          if (!result.performed) {
            snackContext.setMessage(
              "Não foi possível remover este Recurso deste Local.",
              "error"
            );
          } else {
            snackContext.setMessage(
              `Recurso removido deste Local com sucesso`,
              "success"
            );
            props.handleClose();
          }
        })
        .catch(function (error) {
          setIsLoaded(true);
          const msg = getErrorMessage(error);
          snackContext.setMessage(msg, "error");
        });
    }
  }, [props, snackContext]);

  if (props.loadDelete && !isLoaded) return <CircularProgress />;
};

const ResourcesTable = (props) => {
  const [loadDelete, setLoadDelete] = useState(false);
  const [openDeleteConfirmDialog, setOpenDeleteConfirmDialog] = useState(false);

  const [idLocationResource, setIdLocationResource] = useState("");
  const [name, setName] = useState("");

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);

  const userRole = GetUserRole();

  const rows = props.rows;
  const rowsLength = rows.length;

  const pageCallback = (childData) => {
    setPage(childData);
  };

  const rowsPerPageCallback = (childData) => {
    setRowsPerPage(childData);
  };

  //Delete
  const handleClickDelete = (event) => {
    const arr = String(event.currentTarget.id).split("&");
    setIdLocationResource(arr[0]);
    setName(arr[1]);
    setOpenDeleteConfirmDialog(true);
  };
  const handleCloseDeleteDialog = () => {
    setOpenDeleteConfirmDialog(false);
  };
  const handleConfirmDelete = () => {
    setOpenDeleteConfirmDialog(false);
    setLoadDelete(true);
  };
  const handleBack = () => {
    setLoadDelete(false);
    props.handleReloadTable();
  };

  if (rowsLength === 0) {
    return (
      <Typography sx={{ color: "error.main" }}>
        Este local não tem recursos.
      </Typography>
    );
  } else {
    const cells = (
      rowsPerPage > 0
        ? rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
        : rows
    ).map((row) => (
      <StyledTableRow key={row.idLocalRecurso} id={row.idLocalRecurso}>
        <StyledTableCell>{row.nome}</StyledTableCell>
        <StyledTableCell>{row.quantidade}</StyledTableCell>
        <StyledTableCell>{row.pcd === "1" ? "Sim" : "Não"}</StyledTableCell>
        <StyledTableCell>
          {row.obs ? row.obs : "(Não informada)"}
        </StyledTableCell>
        {userRole > 5 && (
          <>
            <StyledTableCell>
              <IconButton
                onClick={props.handleClickEdit}
                id={row.idLocalRecurso}
                sx={{
                  bgcolor: "",
                  "&:hover": {
                    backgroundColor: "warning.light",
                  },
                }}
              >
                <EditIcon />
              </IconButton>
            </StyledTableCell>
            <StyledTableCell>
              <IconButton
                onClick={handleClickDelete}
                id={row.idLocalRecurso + "&" + row.nome}
                sx={{
                  bgcolor: "",
                  "&:hover": {
                    backgroundColor: "error.light",
                  },
                }}
              >
                <DeleteIcon />
              </IconButton>
            </StyledTableCell>
          </>
        )}
      </StyledTableRow>
    ));
    return (
      <Box sx={{ mb: 2 }}>
        <TableContainer component={Paper}>
          <Table size="small" aria-label="data table">
            <TableHead>
              <StyledTableRow>
                <StyledTableCell>Nome</StyledTableCell>
                <StyledTableCell>Qtd.</StyledTableCell>
                <StyledTableCell>PCD</StyledTableCell>
                <StyledTableCell>Observação</StyledTableCell>
                {userRole > 5 && (
                  <>
                    <StyledTableCell sx={{ width: 10 }}>Editar</StyledTableCell>
                    <StyledTableCell sx={{ width: 10 }}>
                      Excluir
                    </StyledTableCell>
                  </>
                )}
              </StyledTableRow>
            </TableHead>
            <TableBody>{cells}</TableBody>
          </Table>
        </TableContainer>
        <Pagination
          name="Recursos"
          length={rows.length}
          pageParentToChild={page}
          rowsPerPageParentToChild={rowsPerPage}
          pageCallback={pageCallback}
          rowsPerPageCallback={rowsPerPageCallback}
        />
        <Box id="boxDelete">
          <DeleteLocationResource
            loadDelete={loadDelete}
            idLocation={props.idLocation}
            idLocationResource={idLocationResource}
            handleClose={handleBack}
          />
          <MessageDialog
            confirm
            variant={MessageDialogVariants.error}
            title="Exclusão"
            open={openDeleteConfirmDialog}
            onClose={handleCloseDeleteDialog}
            onConfirm={handleConfirmDelete}
            content={
              <>
                Você tem certeza que gostaria de remover deste local este
                recurso?
                <br />
                {`Recurso: ${name}`}
              </>
            }
          />
        </Box>
      </Box>
    );
  }
};

const LocationResourceTable = (props) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [items, setItems] = useState([]);

  const [reloadTable, setReloadTable] = useState(false);

  const snackContext = useContext(SnackContext);

  const handleClickEdit = (event) => {
    const resourceItems = items.filter(
      (item) => item.idLocalRecurso === event.currentTarget.id
    );
    props.clickUpdateCallback(resourceItems);
  };

  const handleReloadTable = (event) => {
    setReloadTable(!reloadTable);
  };

  useEffect(() => {
    const fullURL = `local/${props.idLocation}/recurso`;

    instance
      .get(fullURL)
      .then(function (response) {
        setIsLoaded(true);
        const result = response.data;
        const newResult = sortListByName(result);
        setItems(newResult);
      })
      .catch(function (error) {
        setIsLoaded(true);
        const msg = getErrorMessage(error);
        snackContext.setMessage(msg, "error");
      });
  }, [props.idLocation, props.reloadTable, reloadTable, snackContext]);

  if (!isLoaded) return <CircularProgress />;
  else
    return (
      <ResourcesTable
        idLocation={props.idLocation}
        rows={items}
        handleClickEdit={handleClickEdit}
        handleReloadTable={handleReloadTable}
      />
    );
};

export default LocationResourceTable;
